import {useCallback, useEffect} from 'react';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {useVerifyAccountQuery, useVerifyUser} from '#apollo/schema';
import {Screen} from '#component/base';
import {VerifySuccess} from '#component/page/VerifySuccess';
import {useAuth} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {VerifyAccountError} from './component/VerifyAccountError';

/**
 * `VerifyAccount` is the page component for the route linked to from account
 * verification emails (ie, 'verifyaccount/${token}'). It will attempt to verify
 * the account for the given token, and will conditionally render a success or
 * error result.
 */
export const VerifyAccount: NextPage = () => {
  const {isUserSignedIn} = useAuth();
  const {query} = useRouter();
  const authenticated = isUserSignedIn();
  const {data, loading, refetch} = useVerifyAccountQuery({skip: !authenticated});
  const {isVerified, userProfile} = data?.me || {};
  const {token} = query;
  const [verifyUser, {called, data: verifyData, loading: mutationLoading}] = useVerifyUser({
    variables: {input: {token: token as string}},
  });
  const {email} = userProfile || {};
  const {success} = verifyData?.verifyUser || {};
  const shouldAttemptVerification =
    (!authenticated || (!loading && !isVerified)) && token && !called;

  const handleVerifyUser = useCallback(async () => {
    try {
      await verifyUser();
      if (authenticated) {
        refetch();
      }
    } catch (e) {
      console.error('Error', e);
    }
  }, [authenticated, refetch, verifyUser]);

  useEffect(() => {
    if (shouldAttemptVerification) {
      handleVerifyUser();
    }
  }, [handleVerifyUser, shouldAttemptVerification]);

  return (
    <Screen
      contentContainerClassName={sprinkles({
        flex: 'flex',
        content: ['end', 'end', 'center'],
        dir: 'col',
      })}
      loading={loading || mutationLoading}
      pageName="Verify account"
      sidePanelImageUrl={
        success || isVerified ? '/image/verify-account.jpg' : '/image/verification-error.jpg'
      }>
      <div
        className={sprinkles({
          direction: 'col',
          flex: 'flex',
          height: 'full',
          placeContent: ['end', 'center'],
        })}>
        {success || isVerified ? (
          <VerifySuccess authenticated={authenticated} />
        ) : (
          <VerifyAccountError authenticated={authenticated} email={email} />
        )}
      </div>
    </Screen>
  );
};
